var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import { cn } from "@/lib/utils";
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
/** Spec - Tooltips: guard from the screen edge, arrow inset from the tooltip corner */
const TOOLTIP_GUARD = 8;
const ARROW_INSET = 12;
const ARROW_SIZE = 8;
/** On wide elements the arrow points at their first 24px */
const ARROW_TARGET = 24;
/**
 * Arrow x inside the tooltip (px from its left edge); "side" — left/right tooltips,
 * centred vertically; null — not measured yet (hidden)
 */
const ArrowPositionContext = React.createContext("side");
/**
 * Bloom 2.0 tooltip arrow: 8×8 square turned 45°, radius 1, tooltip colour, half out
 * of the tooltip edge. Placed by TooltipContent, so it always points at the element.
 */
const TooltipArrow = React.forwardRef(({ className }, ref) => {
    const x = React.useContext(ArrowPositionContext);
    return (_jsx("span", { ref: ref, "aria-hidden": "true", className: cn("pointer-events-none absolute size-2 rounded-[1px] bg-tooltip", "[[data-side=top]>&]:-bottom-1 [[data-side=bottom]>&]:-top-1 [[data-side=left]>&]:-right-1 [[data-side=right]>&]:-left-1", className), style: x === "side"
            ? { top: "50%", transform: "translateY(-50%) rotate(45deg)" }
            : x === null
                ? { visibility: "hidden" }
                : { left: x, transform: "rotate(45deg)" } }));
});
TooltipArrow.displayName = "TooltipArrow";
/**
 * Bloom 2.0 Spec - Tooltips (reference: trunc-tip.js). On <body>, 8px from the element.
 * Starts at the element's left edge with the arrow on the left; if it would run off the
 * right edge, it lines up with the element's right edge and the arrow moves right.
 * Arrow at least 12px from the corner; fade 140ms.
 */
const TooltipContent = React.forwardRef((_a, ref) => {
    var { className, sideOffset = 8, side = "top", align = "start", collisionPadding = TOOLTIP_GUARD, children } = _a, props = __rest(_a, ["className", "sideOffset", "side", "align", "collisionPadding", "children"]);
    // The Radix content exists only while the tooltip is open: place it when it appears
    const [node, setNode] = React.useState(null);
    const [placedAlign, setPlacedAlign] = React.useState(align);
    const [arrowX, setArrowX] = React.useState(null);
    const setRefs = React.useCallback((el) => {
        setNode(el);
        if (typeof ref === "function")
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    React.useLayoutEffect(() => {
        if (!node) {
            setPlacedAlign(align);
            setArrowX(null);
            return;
        }
        if (side === "left" || side === "right") {
            setPlacedAlign(align);
            setArrowX("side");
            return;
        }
        function place() {
            var _a;
            const el = node;
            if (!el)
                return false;
            // Radix links the open trigger to the tooltip text via aria-describedby
            const id = (_a = el.querySelector('[role="tooltip"]')) === null || _a === void 0 ? void 0 : _a.id;
            const trigger = id
                ? document.querySelector(`[aria-describedby="${CSS.escape(id)}"]`)
                : null;
            if (!trigger)
                return false;
            const t = trigger.getBoundingClientRect();
            const width = el.offsetWidth;
            const viewport = document.documentElement.clientWidth;
            let next = align;
            if (align === "start" && t.left + width > viewport - TOOLTIP_GUARD) {
                next = "end";
            }
            const wanted = next === "end"
                ? t.right - width
                : next === "center"
                    ? t.left + t.width / 2 - width / 2
                    : t.left;
            const left = Math.max(TOOLTIP_GUARD, Math.min(wanted, viewport - TOOLTIP_GUARD - width));
            const reach = Math.min(t.width, ARROW_TARGET) / 2;
            const target = next === "end"
                ? t.right - reach
                : next === "center"
                    ? t.left + t.width / 2
                    : t.left + reach;
            setPlacedAlign(next);
            setArrowX(Math.max(ARROW_INSET, Math.min(target - left - ARROW_SIZE / 2, width - ARROW_INSET - ARROW_SIZE)));
            return true;
        }
        // The trigger gets aria-describedby just after the content mounts: retry briefly
        let timer;
        let tries = 0;
        const run = () => {
            if (!place() && tries++ < 30)
                timer = setTimeout(run, 16);
        };
        run();
        return () => clearTimeout(timer);
    }, [node, align, side]);
    return (
    // Portal: a tooltip must not sit under a sibling layer (sticky headers, z-indexed bars)
    _jsx(TooltipPrimitive.Portal, { children: _jsx(TooltipPrimitive.Content, Object.assign({ ref: setRefs, sideOffset: sideOffset, side: side, align: placedAlign, collisionPadding: collisionPadding, className: cn("pointer-events-none relative z-[10001] rounded-popover border-0 bg-tooltip p-3 text-small font-medium leading-[18px] text-on-contrast shadow-tooltip [text-wrap:pretty] animate-in fade-in-0 duration-[140ms] data-[state=closed]:animate-out data-[state=closed]:fade-out-0", className) }, props, { children: _jsx(ArrowPositionContext.Provider, { value: arrowX, children: children }) })) }));
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
/** Shared tooltip panel styles (Bloom 2.0 Spec - Tooltips: dark, radius 12, padding 12, Inter 13/18 500). */
export const TOOLTIP_CONTENT_CLASS = "max-w-[240px] rounded-nav border-0 bg-tooltip px-3 py-2 text-small font-medium leading-[18px] text-on-contrast shadow-tooltip [text-wrap:pretty] [overflow-wrap:anywhere]";
export const TOOLTIP_CONTENT_MULTILINE_CLASS = cn(TOOLTIP_CONTENT_CLASS, "max-w-[min(240px,calc(100vw-3rem))] whitespace-pre-line text-left");
/** Standard app tooltip: arrow, dark tooltip background, 8px gap, 200ms delay. */
function AppTooltip({ children, content, side = "top", align = "start", multiline = false, }) {
    return (_jsx(TooltipProvider, { delayDuration: 200, children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: children }), _jsxs(TooltipContent, { side: side, align: align, sideOffset: 8, className: multiline ? TOOLTIP_CONTENT_MULTILINE_CLASS : TOOLTIP_CONTENT_CLASS, children: [_jsx(TooltipArrow, {}), content] })] }) }));
}
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipArrow, AppTooltip, };
